import logger from './logger';

/**
 * Mention Processing Utility for Matrix Messages
 *
 * Provides functions to extract @mentions from message text, resolve them
 * to Matrix user IDs, and enrich message content with MSC3952-compliant
 * m.mentions field and HTML pills.
 */

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Extract @mention patterns from plain text body
 *
 * Supports:
 * - Simple mentions: @username
 * - Bracketed mentions: @[User Name] (allows spaces/special chars)
 * - Hash discriminators: @username#1234 or @[User Name]#1234
 *
 * Excludes:
 * - Email addresses (word char before @)
 * - Mentions inside code blocks (``` or `)
 *
 * @param {string} body - Message body text
 * @returns {Array<{text: string, displayName: string, hash: string|null, index: number}>} Array of mention objects with positions
 */
export function extractMentionsFromBody(body) {
  if (!body || typeof body !== 'string') return [];

  // Build a map of code block positions to exclude
  const codeBlockRanges = [];
  const fencedRegex = /```[\s\S]*?```/g;
  const inlineRegex = /`[^`]+`/g;
  let codeMatch;
  while ((codeMatch = fencedRegex.exec(body)) !== null) {
    codeBlockRanges.push({ start: codeMatch.index, end: codeMatch.index + codeMatch[0].length });
  }
  while ((codeMatch = inlineRegex.exec(body)) !== null) {
    codeBlockRanges.push({ start: codeMatch.index, end: codeMatch.index + codeMatch[0].length });
  }

  // Check if a position is inside a code block
  const isInCodeBlock = (pos) => codeBlockRanges.some(r => pos >= r.start && pos < r.end);

  // Regex: @username, @[User Name], @user#1234
  // Group 1: bracketed name (without brackets)
  // Group 2: simple username (word chars)
  // Group 3: optional hash discriminator
  const mentionRegex = /@(?:\[([^\]]+)\]|(\w+))(?:#(\d+))?/g;
  const mentions = [];
  let match;

  while ((match = mentionRegex.exec(body)) !== null) {
    // Skip if inside code block
    if (isInCodeBlock(match.index)) continue;

    // Skip if preceded by word char (email detection: user@domain.com)
    const precedingText = body.substring(0, match.index);
    if (/\w$/.test(precedingText)) continue;

    mentions.push({
      text: match[0],                       // Full match: @alice, @[John Doe], @user#1234
      displayName: match[1] || match[2],    // Bracketed name or simple username
      hash: match[3] || null,               // Optional hash discriminator
      index: match.index,                   // Position in original body for duplicate handling
    });
  }

  logger.debug('[mentionProcessing] Extracted mentions:', mentions);
  return mentions;
}

/**
 * Resolve extracted mentions to Matrix user IDs using room member lookup
 *
 * Performs case-insensitive matching against:
 * 1. Member display names (priority)
 * 2. userId localpart (fallback, e.g., "@mat_user2:server" → "mat_user2")
 *
 * Uses hash discriminator for disambiguation when multiple members
 * share the same display name or localpart.
 *
 * Returns an array to preserve all mention occurrences (including duplicates).
 *
 * @param {Array<{text: string, displayName: string, hash: string|null, index: number}>} mentions - Extracted mentions
 * @param {Array<{userId: string, name?: string, rawDisplayName?: string}>} roomMembers - Room members
 * @returns {Array<{text: string, userId: string|null, index: number}>} Array of resolved mentions with positions
 */
export function resolveMentionsToUserIds(mentions, roomMembers) {
  if (!mentions?.length || !roomMembers?.length) return [];

  // Build case-insensitive lookup: normalizedDisplayName → [members]
  const nameLookup = new Map();
  // Build localpart lookup: normalizedLocalpart → [members]
  const localpartLookup = new Map();

  for (const member of roomMembers) {
    const name = member.rawDisplayName || member.name;
    if (name) {
      const normalizedName = name.toLowerCase();
      if (!nameLookup.has(normalizedName)) {
        nameLookup.set(normalizedName, []);
      }
      nameLookup.get(normalizedName).push(member);
    }

    // Extract localpart from userId (e.g., "@mat_user2:server.com" → "mat_user2")
    if (member.userId) {
      const localpart = member.userId.split(':')[0].slice(1).toLowerCase();
      if (!localpartLookup.has(localpart)) {
        localpartLookup.set(localpart, []);
      }
      localpartLookup.get(localpart).push(member);
    }
  }

  // Resolve each mention (preserving all occurrences including duplicates)
  const result = [];
  for (const mention of mentions) {
    // Check if displayName contains an embedded MXID: "Name (@user:server)"
    const embeddedMxidMatch = mention.displayName.match(/\s*\((@[^)]+)\)\s*$/);
    if (embeddedMxidMatch) {
      const embeddedMxid = embeddedMxidMatch[1];
      const match = roomMembers.find(m => m.userId === embeddedMxid);
      if (match) {
        result.push({ text: mention.text, userId: match.userId, index: mention.index });
        continue;
      }
    }

    const normalizedName = mention.displayName.toLowerCase();

    // Try display name lookup first (priority)
    let candidates = nameLookup.get(normalizedName) || [];

    // Fallback to localpart lookup if no display name match
    if (candidates.length === 0) {
      candidates = localpartLookup.get(normalizedName) || [];
    }

    let userId = null;
    if (candidates.length === 1) {
      // Single match - resolve directly
      userId = candidates[0].userId;
    } else if (candidates.length > 1 && mention.hash) {
      // Multiple matches - use hash to disambiguate
      // Match against server domain specifically (after the : in userId)
      // Use exact match or prefix match to avoid false positives
      // e.g., hash "server" should NOT match "my-server.com"
      const match = candidates.find(m => {
        const server = m.userId.split(':')[1];
        if (!server) return false;
        // Exact match (e.g., hash "server.com" matches "server.com")
        // or prefix match with dot (e.g., hash "server1" matches "server1.org")
        return server === mention.hash || server.startsWith(mention.hash + '.');
      });
      userId = match?.userId || null;
    }
    // If no match or ambiguous without hash, userId stays null

    result.push({
      text: mention.text,
      userId,
      index: mention.index,
    });
  }

  logger.debug('[mentionProcessing] Resolved mentions:', result);
  return result;
}

/**
 * Build HTML formatted_body with Matrix.to anchor pills
 *
 * Replaces resolved mentions with clickable Matrix pill links.
 * Unresolved mentions are kept as escaped plain text.
 * All surrounding text is HTML-escaped to prevent XSS.
 *
 * @param {string} body - Original message body
 * @param {Array<{text: string, userId: string|null, index: number}>} resolvedMentions - Array of resolved mentions with positions
 * @returns {string} HTML formatted body with pills
 */
export function buildFormattedBody(body, resolvedMentions) {
  if (!body || !resolvedMentions?.length) return body;

  let result = '';
  let lastIndex = 0;

  // Sort mentions by position in body (already have positions from extraction)
  const sortedMentions = [...resolvedMentions].sort((a, b) => a.index - b.index);

  for (const { text, userId, index } of sortedMentions) {
    // Add escaped text before this mention
    result += escapeHtml(body.slice(lastIndex, index));

    if (userId) {
      // Create Matrix pill for resolved mention
      // Extract display text: @[John Doe] → John Doe, @alice → alice
      let displayText = text.startsWith('@[')
        ? text.slice(2, text.lastIndexOf(']'))
        : text.slice(1).replace(/#\d+$/, ''); // Remove hash suffix
      // Strip embedded MXID suffix like " (@dupe1:server.com)" from display text
      const mxidSuffix = displayText.match(/\s*\(@[^)]+\)\s*$/);
      if (mxidSuffix) {
        displayText = displayText.slice(0, -mxidSuffix[0].length);
      }
      result += `<a href="https://matrix.to/#/${userId}">${escapeHtml(displayText)}</a>`;
    } else {
      // Unresolved: keep as plain escaped text
      result += escapeHtml(text);
    }

    lastIndex = index + text.length;
  }

  // Add remaining text after last mention
  result += escapeHtml(body.slice(lastIndex));

  logger.debug('[mentionProcessing] Built formatted_body');
  return result;
}

/**
 * Enrich message content with mentions per MSC3952 spec
 *
 * Main entry point that orchestrates:
 * 1. Extract mentions from content.body
 * 2. Resolve to Matrix user IDs
 * 3. Filter out self-mentions (if currentUserId provided)
 * 4. Add format: 'org.matrix.custom.html'
 * 5. Add formatted_body with HTML pills (self-mentions rendered as plain text)
 * 6. Add m.mentions: { user_ids: [...] } for resolved mentions (deduplicated, excluding self)
 *
 * @param {Object} content - Message content object with body property
 * @param {Array<{userId: string, name?: string, rawDisplayName?: string}>} roomMembers - Room members
 * @param {string|null} currentUserId - Current user's Matrix ID to exclude from mentions (prevents self-mentions)
 * @returns {Object} Enriched content object (original not mutated)
 */
export function enrichEventWithMentions(content, roomMembers, currentUserId = null) {
  if (!content?.body || !roomMembers?.length) return content;

  // Step 1: Extract mentions from body (includes position for duplicate handling)
  const mentions = extractMentionsFromBody(content.body);
  if (!mentions.length) return content;

  // Step 2: Resolve mentions to Matrix user IDs (array preserves all occurrences)
  const resolvedMentions = resolveMentionsToUserIds(mentions, roomMembers);

  // Step 3: Filter out self-mentions - set userId to null so they render as plain text
  const filteredMentions = resolvedMentions.map(mention => {
    if (currentUserId && mention.userId === currentUserId) {
      // Self-mention: clear the userId so it renders as plain text, not a pill
      return { ...mention, userId: null };
    }
    return mention;
  });

  // Step 4: Build HTML formatted body with pills (self-mentions now rendered as plain text)
  const formattedBody = buildFormattedBody(content.body, filteredMentions);

  // Step 5: Extract unique resolved user IDs (deduplicated per MSC3952, excluding self)
  const resolvedUserIds = [...new Set(
    filteredMentions
      .map(m => m.userId)
      .filter(id => id !== null),
  )];

  // Step 6: Build enriched content (shallow copy to avoid mutation)
  const enriched = { ...content };
  enriched.format = 'org.matrix.custom.html';
  enriched.formatted_body = formattedBody;

  // Add m.mentions per MSC3952 (only if resolved mentions exist)
  if (resolvedUserIds.length > 0) {
    enriched['m.mentions'] = { user_ids: resolvedUserIds };
  }

  logger.debug('[mentionProcessing] Enriched event with mentions:', {
    mentionCount: mentions.length,
    resolvedCount: resolvedUserIds.length,
  });

  return enriched;
}
